import type { DateTime } from 'luxon';
import { merge } from 'lodash';
import axios from 'axios';

/**
 * SectionsService
 */
export default class SectionsService
{
    public static path = 'admin/wk/sections';

    /**
     * @param filter FilterModel
     * @param pager Pager
     *
     * @returns Promise<Pagination<Resource<ListItemModel>>>
     */
    public static async getList(filter: FilterModel, pager: Pager): Promise<Pagination<Resource<ListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>(this.path, {
            params: merge({}, filter, pager.data())
        })).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async fetch(id: number): Promise<Resource<FormModel>>
    {
        return (await axios.get<Resource<FormModel>>(`${this.path}/${id}`)).data;
    }

    /**
     * @param model FormModel
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async create(model: FormModel): Promise<Resource<FormModel>>
    {
        return (await axios.post<Resource<FormModel>>(this.path, model)).data;
    }

    /**
     * @param id number
     * @param model FormModel
     *
     * @returns Promise<Statement>
     */
    public static async update(id: number, model: FormModel): Promise<Statement>
    {
        return (await axios.put<Statement>(`${this.path}/${id}`, model)).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<Statement>
     */
    public static async remove(id: number): Promise<Statement>
    {
        return (await axios.delete(`${this.path}/${id}`)).data as Statement;
    }

    /**
     * @param search string
     * @param limit number
     * @param exept number[]
     *
     * @returns Promise<string[]>
     */
    public static async getAutocomplete(search: string, limit: number = 10, exept?: number[], forArticles?: boolean): Promise<ListItemModel[]>
    {
        return (await axios.get<ListItemModel[]>(`${this.path}/autocomplete`, {
            params: {
                search: search,
                limit: limit,
                exept: exept,
                forArticles: forArticles
            }
        })).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<ListItemModel>
     */
    public static async fetchAutocomplete(id: number): Promise<ListItemModel>
    {
        return (await axios.get<ListItemModel>(`${this.path}/autocomplete/${id}`)).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<ListItemModel>
     */
    public static async getListByIds(ids: number[]): Promise<ListItemModel[]>
    {
        return (await axios.post<ListItemModel[]>(`${this.path}/ids`, ids)).data;
    }

    public static async getAll(): Promise<ListItemModel[]>
    {
        return (await axios.get<ListItemModel[]>(`${this.path}/all`)).data;
    }
}

export interface ListItemModel
{
    id: number;
    name: string;
    labelsQty: number;
    topicsQty: number;
    isPublished: boolean;
    dateCreatedUtc: DateTime;
    dateModifiedUtc?: DateTime;
    pageId?: number;
}

export interface FormModel
{
    id: number;
    name: string;
    isPublished: boolean;
    hideInSearch: boolean;
    labelsIds: number[];
    topicsIds: number[];
}

export interface FilterModel
{
    name: string;
    labelId?: number;
    topicId?: number;
}
